import type { GitpodAPI } from "@/api";
import { createBatchLoader, type BatchLoader } from "@/queries/dataloader";
import { autoPaginate } from "@/queries/pagination";
import { roleToPermissions, type Permission } from "@/utils/permissions.data";
import { toPlainMessage } from "@bufbuild/protobuf";
import { type ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

/**
 * Permissions resolved for a single (resourceType, resourceId) pair.
 */
export type ResourcePermissionSet = ReadonlySet<Permission>;

export type PermissionLoader = BatchLoader<ResourcePermissionSet>;

/**
 * Encode a (resourceType, resourceId) pair into a batch loader key.
 */
export function encodePermissionKey(resourceType: ResourceType, resourceId: string): string {
    return `${resourceType}:${resourceId}`;
}

/**
 * Decode a batch loader key back into (resourceType, resourceId).
 */
function decodePermissionKey(key: string): { resourceType: ResourceType; resourceId: string } {
    const colonIdx = key.indexOf(":");
    return {
        resourceType: Number(key.slice(0, colonIdx)) as ResourceType,
        resourceId: key.slice(colonIdx + 1),
    };
}

const createPermissionLoader = (api: GitpodAPI, userId: string): PermissionLoader =>
    createBatchLoader(async (keys) => {
        const results = new Map<string, ResourcePermissionSet>();

        // Decode keys to extract unique resource IDs and types
        const decoded = keys.map(decodePermissionKey);
        const resourceIds = [...new Set(decoded.map((d) => d.resourceId))];
        const resourceTypes = [...new Set(decoded.map((d) => d.resourceType))];

        const assignments = await autoPaginate(
            (params) =>
                api.groupService.listRoleAssignments({
                    ...params,
                    filter: { userId, resourceIds, resourceTypes },
                }),
            {},
            (response) => response.assignments,
            100,
        );

        // Build a map of key -> Set<Permission> from the response
        for (const assignment of assignments) {
            const plain = toPlainMessage(assignment);
            const key = encodePermissionKey(plain.resourceType, plain.resourceId);

            let perms = results.get(key);
            if (!perms) {
                perms = new Set<Permission>();
                results.set(key, perms);
            }

            const rolePerms = roleToPermissions[plain.resourceRole];
            if (rolePerms) {
                for (const p of rolePerms) {
                    (perms as Set<Permission>).add(p);
                }
            }
        }

        // Ensure every requested key has an entry (empty set if no assignments)
        for (const key of keys) {
            if (!results.has(key)) {
                results.set(key, new Set<Permission>());
            }
        }

        return results;
    }, 25);

/**
 * Module-level singleton loader, shared across all components.
 *
 * Every component that calls getPermissionLoader() gets the same instance
 * (per user+org), enabling cross-component batching within a render cycle.
 */
let sharedLoader: { loader: PermissionLoader; userId: string; organizationId: string; api: GitpodAPI } | null = null;

export const getPermissionLoader = (api: GitpodAPI, userId: string, organizationId: string): PermissionLoader => {
    if (
        !sharedLoader ||
        sharedLoader.userId !== userId ||
        sharedLoader.organizationId !== organizationId ||
        sharedLoader.api !== api
    ) {
        sharedLoader = {
            loader: createPermissionLoader(api, userId),
            userId,
            organizationId,
            api,
        };
    }

    return sharedLoader.loader;
};
